import { useQuery } from '@tanstack/react-query'
import {
  DataTable,
  DataTableProvider,
  useDataTableContext,
} from 'components/DataTable'
import { ExportButton } from 'components/Buttons/ExportButton'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import PipelineExportModal from 'components/Modals/ExportModal/PipelineExportModal'
import { useMemo, useState } from 'react'
import { PiDownloadSimple } from 'react-icons/pi'
import useApiService from 'services/api'
import { getReportsDealsPipeline } from 'services/reports'
import type { DealsPipeline } from 'models/deals_pipeline'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { usePaginationURLParams } from 'utils/usePaginationURLParams'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { FBPageTableContainer } from '../../FbUI/Page/styles'
import DealsPipelineTableFilters from './DealsPipelineFilterset'
import { useDealsPipelineTableColumns } from './useDealsPipelineTableColumns'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { TableSearch } from 'components/Filters/TableSearch'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { cn } from 'components/UI/cn'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { ToolbarDensityProvider } from 'components/Tables/CommonTableCells/ToolbarDensityContext'
import * as S from '../CommonTable.styles'

// Default columns: Company, Deals, Sales Stages, Last Update, Annual Revenue Opportunity, Annual Volume Opportunity
const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  closed: false,
  taro_closed: false,
  days_since_last_modified: false,
  annual_volume_closed: false,
}

interface DealsPipelineTableProps {
  tableKey: string
}

const PAGE_SIZE = 20
const IDENTIFIER = 'DEALS_PIPELINE_TABLE'

function DealsPipelineTableComponent(props: DealsPipelineTableProps) {
  const { enabledReportDeals } = useFeatureFlagService()
  const { sorting } = dataTableSortingStoreRepo.getStore(props.tableKey)()
  const {
    methods: { clearSelectedRows },
  } = useDataTableContext()
  const api = useApiService()
  const dealsPipelineFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const filtersAsParams = useFilterParams(dealsPipelineFilterStore)
  const [pagination] = usePaginationURLParams(PAGE_SIZE)
  const [showExportModal, setShowExportModal] = useState(false)
  const filterParams = useMemo(() => {
    const params: Record<string, any> = filtersAsParams

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting, filtersAsParams])

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterParams,
    page: pagination.pageIndex,
  })

  // DATA FETCHING
  const { isFetching, data } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: async ({ signal }) => {
      clearSelectedRows()

      const fullParams = {
        ...filterParams,
        page: pagination.pageIndex + 1,
        has_stage: true,
      }

      if (enabledReportDeals) {
        // `reports/dashboard/deals-pipeline/` (`BaseDealsPipeline.get_deals_pipeline`)
        // never reads `page`/`has_stage` -- it already returns every matching
        // row unpaginated (this table relies on `virtualizeRows` for
        // client-side scrolling), which is also why the generated
        // `GetReportsDealsPipelineQueryParams` doesn't declare either field.
        // `filterParams` (not `fullParams`) is passed through as-is.
        //
        // The generated `ReportsDealsPipelineRow` differs from the
        // hand-written `DealsPipeline` model consumed by
        // `useDealsPipelineTableColumns`/`useCompanyUrlFromDealPipeline`
        // (nullable polymorphic ids, no `chain_name`/`product`/etc. --
        // fields those two aren't in this task's scope don't read anyway) --
        // cast at the fetch boundary rather than threading a second row type
        // through them, mirroring the deals-consumer migration's approach.
        return (await getReportsDealsPipeline(filterParams, {
          signal,
        })) as unknown as DealsPipeline[]
      }

      return await api.getDealsPipeline(fullParams, signal)
    },
  })

  const columns = useDealsPipelineTableColumns()

  return (
    <>
      <h2 className="mb-3 text-lg font-semibold text-fg">
        Companies in Pipeline
      </h2>

      <TableLinkStyleProvider variant="plain">
        <FBPageTableContainer
          className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
          style={{ height: '800px' }}
        >
          {/* sm toolbar density (32px search + Filter/Columns), matching the
              other tables. Wraps DataTable so it also covers the portaled
              Columns button. */}
          <ToolbarDensityProvider value="sm">
            <DataTable
              tableHeader={
                <S.SearchContainer>
                  <div className="flex w-full gap-2 items-center">
                    <TableSearch
                      className="flex-1"
                      filterStore={dealsPipelineFilterStore}
                      searchPlaceholder="Type chain name"
                      tableKey={props.tableKey}
                    />
                    <DealsPipelineTableFilters identifier={IDENTIFIER} />

                    <ColumnSelectorRecipient tableKey={props.tableKey} />
                  </div>

                  <div className={cn('flex flex-row justify-between gap-4')}>
                    <FilterChips
                      identifier={IDENTIFIER}
                      store={dealsPipelineFilterStore}
                      showActive
                      clearAllButton
                    />
                  </div>
                </S.SearchContainer>
              }
              tableKey={props.tableKey}
              loading={isFetching}
              data={data ?? []}
              columns={columns}
              sortableFields="all"
              virtualizeRows
              enableRowSelection={false}
              enableColumnResizing
              rowHeight={44}
              headerHeight={40}
              defaultSort={[{ id: 'last_modified' as any, desc: true }]}
              footerControls={
                <ExportButton
                  variant="primary"
                  className="!bg-brand-bright !text-white hover:!bg-brand-active"
                  onClick={() => setShowExportModal(true)}
                >
                  <PiDownloadSimple size={18} />
                </ExportButton>
              }
            />
          </ToolbarDensityProvider>
        </FBPageTableContainer>
      </TableLinkStyleProvider>

      <PipelineExportModal
        show={showExportModal}
        handleClose={() => setShowExportModal(false)}
        filters={filterParams}
      />
    </>
  )
}

export function DealsPipelineTable(props: DealsPipelineTableProps) {
  const { tableKey, ...rest } = props

  return (
    <ColumnsStoreProvider
      savedPresetsIdentifier={'DEALS_PIPELINE_TABLE'}
      tableKey={tableKey}
      defaultColumnVisibility={DEFAULT_COLUMN_VISIBILITY}
    >
      <DataTableProvider tableKey={tableKey}>
        <DealsPipelineTableComponent tableKey={tableKey} {...rest} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
